import { NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronLeft,
  FolderOpen,
  GitBranch,
  LayoutDashboard,
  MessageSquare,
  Milestone,
  Settings,
  UsersRound,
} from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { projectsApi } from '@/features/projects/api';
import { usePermission } from '@/hooks/usePermission';
import type { Project } from '@/features/projects/types';

interface Tab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  permission?: string;
}

const tabs: Tab[] = [
  { to: '', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: 'milestones', label: 'Milestones', icon: Milestone },
  { to: 'members', label: 'Members', icon: UsersRound },
  { to: 'files', label: 'Files', icon: FolderOpen, permission: 'documents.view' },
  { to: 'calendar', label: 'Calendar', icon: Calendar },
  { to: 'discussion', label: 'Discussion', icon: MessageSquare },
  { to: 'risks', label: 'Risks', icon: AlertTriangle },
  { to: 'dependencies', label: 'Dependencies', icon: GitBranch },
  { to: 'reports', label: 'Reports', icon: BarChart3, permission: 'reports.view' },
  { to: 'settings', label: 'Settings', icon: Settings, permission: 'projects.update' },
];

/** Tab shell for a single project — fetches the project once and hands it to nested pages via <Outlet context>. */
export function ProjectLayout() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();

  const { data: project, isLoading } = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => projectsApi.get(projectId),
  });

  if (isLoading || !project) {
    return <Spinner label="Loading project…" />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/projects')}
          aria-label="Back to projects"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg"
          style={{ backgroundColor: (project.color ?? '#6366f1') + '20', color: project.color ?? '#6366f1' }}
        >
          {project.icon ?? project.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">{project.name}</h1>
          <p className="truncate text-sm text-gray-500 dark:text-gray-400">{project.code} · {project.workspace_name}</p>
        </div>
        {project.is_archived && <Badge tone="gray">Archived</Badge>}
        {project.status_name && <Badge tone="blue">{project.status_name}</Badge>}
        {project.priority_name && <Badge tone="amber">{project.priority_name}</Badge>}
      </div>

      <div className="-mb-px flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
        {tabs.map((tab) => (
          <TabLink key={tab.to} tab={tab} />
        ))}
      </div>

      <Outlet context={project} />
    </div>
  );
}

function TabLink({ tab }: { tab: Tab }) {
  const allowed = usePermission(tab.permission ?? '__always__') || !tab.permission;
  if (!allowed) {
    return null;
  }

  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) =>
        `flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
        }`
      }
    >
      <tab.icon className="h-4 w-4" />
      {tab.label}
    </NavLink>
  );
}

/** Nested project pages call this instead of re-fetching the project themselves. */
export function useProjectContext(): Project {
  return useOutletContext<Project>();
}
